/**
 * Lecture d'un jeu de critères de liste.
 *
 * Les écrans décrivent leurs critères par un objet « vide » — la valeur de
 * repos de chaque champ. Tout ce qui s'en écarte est un filtre actif: c'est ce
 * qui alimente le compteur du panneau, les pastilles, et ce qu'on enregistre
 * sous un nom.
 */

/** @returns {boolean} vrai si la valeur ne filtre rien. */
export function isEmptyValue(value) {
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (value && typeof value === 'object') {
    return Object.values(value).every((item) => isEmptyValue(item));
  }

  return value === null || value === undefined || value === '';
}

/** @returns {string[]} les champs qui filtrent réellement quelque chose. */
export function activeFilterKeys(filters, emptyFilters) {
  return Object.keys(emptyFilters).filter((key) => {
    const value = filters?.[key];

    if (isEmptyValue(value)) {
      return false;
    }

    // Un champ revenu a sa valeur de repos ne filtre pas, meme s'il n'est pas
    // « vide »: un tri par defaut, par exemple.
    return JSON.stringify(value) !== JSON.stringify(emptyFilters[key]);
  });
}

/** @returns {number} combien de champs filtrent, pour le compteur du panneau. */
export function countActiveFilters(filters, emptyFilters) {
  return activeFilterKeys(filters, emptyFilters).length;
}

/**
 * Le jeu de critères tel qu'on l'enregistre: seulement ce qui filtre.
 *
 * Garder les champs au repos figerait la forme d'aujourd'hui; un préréglage
 * rappelé plus tard reposerait alors des valeurs vides sur des champs qui
 * n'existent peut-être plus.
 */
export function toCriteria(filters, emptyFilters) {
  return activeFilterKeys(filters, emptyFilters).reduce((acc, key) => {
    acc[key] = filters[key];
    return acc;
  }, {});
}

/**
 * Le jeu de critères relu: on repart du vide et on ne reprend que les champs
 * que l'écran connaît encore. Un critère enregistré sur un champ disparu est
 * ignoré plutôt que de rouvrir l'écran dans un état qu'il ne sait plus régler.
 */
export function fromCriteria(criteria, emptyFilters) {
  const out = { ...emptyFilters };

  Object.keys(emptyFilters).forEach((key) => {
    if (criteria && Object.prototype.hasOwnProperty.call(criteria, key)) {
      out[key] = criteria[key];
    }
  });

  return out;
}
